import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { listCompanyUsers, createEmployee, updateUser, sendInvitationApi, listLocations } from "../api";
import { ROLES, ROLE_LABELS } from "../constants";
import { solveGrossFromNet } from "../controllers/taxes";
import ImportEmployeesModal from "./ImportEmployeesModal";
import Invitations from "./Invitations";
import EmploymentHistory from "./EmploymentHistory";
import AppPage from "../components/AppPage";

const EMPLOYEE_TABS = ["list", "invitations", "history"];

const blankForm = {
  email: "", name: "", position: "",
  role: ROLES.EMPLOYEE, salary: 200000, week_type: 5, manager_id: "", location_id: "",
  salary_mode: "gross", social_status_id: "",
};

export default function Employees() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "list";
  const tab = EMPLOYEE_TABS.includes(rawTab) ? rawTab : "list";

  const setTab = (name) => {
    if (name === "list") setSearchParams({}, { replace: true });
    else setSearchParams({ tab: name }, { replace: true });
  };

  return (
    <AppPage testId="employees-page" bodyScroll="scroll">
      <h4 className="fw-bold mb-3">{t("employees.title")}</h4>

      <ul className="nav nav-tabs mb-3" data-testid="employees-hub-tabs">
        <li className="nav-item">
          <button type="button"
                  className={`nav-link ${tab === "list" ? "active" : ""}`}
                  onClick={() => setTab("list")}
                  data-testid="tab-employees-list">
            <i className="bi bi-people me-1"></i>{t("nav.employees")}
          </button>
        </li>
        <li className="nav-item">
          <button type="button"
                  className={`nav-link ${tab === "invitations" ? "active" : ""}`}
                  onClick={() => setTab("invitations")}
                  data-testid="tab-employees-invitations">
            <i className="bi bi-envelope-paper me-1"></i>{t("nav.invitations")}
          </button>
        </li>
        <li className="nav-item">
          <button type="button"
                  className={`nav-link ${tab === "history" ? "active" : ""}`}
                  onClick={() => setTab("history")}
                  data-testid="tab-employees-history">
            <i className="bi bi-clock-history me-1"></i>{t("nav.history")}
          </button>
        </li>
      </ul>

      {tab === "list" && <EmployeesListTab />}
      {tab === "invitations" && <Invitations embedded />}
      {tab === "history" && <EmploymentHistory embedded />}
    </AppPage>
  );
}

function EmployeesListTab() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [canInvite, setCanInvite] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showInviteEmail, setShowInviteEmail] = useState(false);
  const [bindTarget, setBindTarget] = useState(null);  // user object for bind-invite modal

  const reload = async () => {
    const [u, locs] = await Promise.all([listCompanyUsers(), listLocations().catch(() => [])]);
    setUsers(u); setLocations(locs || []);
  };
  useEffect(() => { reload(); }, []);

  const filteredUsers = locationFilter === "all"
    ? users
    : locationFilter === "_none"
      ? users.filter(u => !u.location_id)
      : users.filter(u => u.location_id === locationFilter);

  const managers = users.filter(u => u.role === ROLES.MANAGER || u.is_manager);

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg(""); setCanInvite(false);
    try {
      const trimmedEmail = (form.email || "").trim();
      await createEmployee({
        email: trimmedEmail || null,
        name: form.name,
        position: form.position,
        role: form.role,
        salary: Number(form.salary),
        week_type: Number(form.week_type),
        manager_id: form.manager_id || null,
        location_id: form.location_id || null,
        salary_mode: form.salary_mode || "gross",
        social_status_id: form.social_status_id || null,
      });
      setForm(blankForm);
      setShowForm(false);
      setMsg(trimmedEmail
        ? "Сотрудник создан. Он сможет войти по e-mail и паролю или установить пароль через «Забыли пароль»."
        : "Сотрудник создан без e-mail. Вы сможете отправить ему приглашение позже из списка сотрудников.");
      await reload();
    } catch (e2) {
      const detail = e2.response?.data?.detail || "Ошибка создания";
      setErr(detail);
      // If conflict is because user exists in another / no company → offer to send invitation
      const status = e2.response?.status;
      if (status === 409 && /другой компании|уже зарегистрирован/i.test(detail)) {
        setCanInvite(true);
      }
    } finally { setBusy(false); }
  };

  const onSendInvitation = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await sendInvitationApi({
        email: form.email.trim(),
        name: form.name,
        position: form.position,
        role: form.role,
        salary: Number(form.salary),
        week_type: Number(form.week_type),
        message: inviteMessage,
      });
      setMsg(`Приглашение отправлено на ${form.email.trim()}. Сотрудник увидит его в портале (раздел «Приглашения») и получит email.`);
      setCanInvite(false);
      setInviteMessage("");
      setForm(blankForm);
      setShowForm(false);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Ошибка отправки приглашения");
    } finally { setBusy(false); }
  };

  const onChangeRole = async (uid, role) => {
    await updateUser(uid, { role });
    await reload();
  };
  const onChangeManager = async (uid, manager_id) => {
    await updateUser(uid, { manager_id: manager_id || null });
    await reload();
  };
  const onChangeSalary = async (uid, salary) => {
    await updateUser(uid, { salary: Number(salary) });
    await reload();
  };
  const onChangeLocation = async (uid, location_id) => {
    await updateUser(uid, { location_id: location_id || null });
    await reload();
  };
  const onChangeSocialStatus = async (uid, social_status_id) => {
    await updateUser(uid, { social_status_id: social_status_id || null });
    await reload();
  };
  const onChangeVacationDays = async (uid, value) => {
    const n = value === "" ? null : Number(value);
    await updateUser(uid, { vacation_days_limit: n });
    await reload();
  };

  const onChangeSalaryMode = async (uid, mode) => {
    await updateUser(uid, { salary_mode: mode });
    await reload();
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3 flex-wrap gap-2">
        <button className="btn btn-outline-success" onClick={() => setShowImport(true)}
                data-testid="import-employees-btn">
            <i className="bi bi-file-earmark-spreadsheet me-1"></i>{t("employees.import_excel")}
          </button>
          <button className="btn btn-outline-primary" onClick={() => setShowInviteEmail(true)}
                  data-testid="invite-by-email-btn">
            <i className="bi bi-envelope-plus me-1"></i>{t("employees.invite_email")}
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} data-testid="add-employee-btn">
            <i className="bi bi-person-plus me-1"></i>{showForm ? t("employees.hide_form") : t("employees.add_employee")}
          </button>
      </div>

      <ImportEmployeesModal
        show={showImport}
        onClose={() => setShowImport(false)}
        onImported={reload}
      />

      {showInviteEmail && (
        <InviteByEmailModal
          onClose={() => setShowInviteEmail(false)}
          onSent={(email) => {
            setShowInviteEmail(false);
            setMsg(t("employees.invite_email_sent", { email }));
          }}
        />
      )}

      {bindTarget && (
        <BindInviteModal
          target={bindTarget}
          onClose={() => setBindTarget(null)}
          onSent={(email) => {
            setBindTarget(null);
            setMsg(t("employees.invite_email_sent", { email }));
          }}
        />
      )}

      {err && <div className="alert alert-danger py-2" data-testid="emp-error">{err}</div>}
      {canInvite && (
        <div className="alert alert-warning" data-testid="emp-invite-prompt">
          <div className="d-flex align-items-start gap-2 mb-2">
            <i className="bi bi-info-circle fs-5"></i>
            <div className="flex-grow-1">
              <strong>Можно отправить приглашение.</strong> Заполненные данные (должность, оклад, роль) будут предложены пользователю. Он сможет принять или отклонить.
            </div>
          </div>
          <div className="row g-2 align-items-end">
            <div className="col-md-8">
              <label className="form-label small mb-1">Сообщение (необязательно)</label>
              <input className="form-control form-control-sm" value={inviteMessage}
                     onChange={(e) => setInviteMessage(e.target.value)}
                     placeholder="Напр.: Будем рады видеть вас в нашей команде!"
                     data-testid="invite-message" />
            </div>
            <div className="col-md-4 d-flex gap-2">
              <button className="btn btn-primary btn-sm" disabled={busy}
                      onClick={onSendInvitation} data-testid="send-invite-btn">
                <i className="bi bi-envelope-paper me-1"></i>Отправить приглашение
              </button>
              <button className="btn btn-link btn-sm text-muted"
                      onClick={() => { setCanInvite(false); setInviteMessage(""); }}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
      {msg && <div className="alert alert-success py-2" data-testid="emp-success">{msg}</div>}

      {showForm && (
        <div className="card card-soft mb-4" data-testid="employee-form"><div className="card-body">
          <form onSubmit={onCreate} className="row g-3">
            <div className="col-md-4">
              <label className="form-label small">ФИО</label>
              <input className="form-control" required name="name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
            </div>
            <div className="col-md-4">
              <label className="form-label small">
                E-mail <span className="text-muted">({t("common.optional")})</span>
              </label>
              <input type="email" className="form-control" name="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email"
                     placeholder={t("employees.email_optional_placeholder")} />
              <div className="small text-muted mt-1">
                <i className="bi bi-info-circle me-1"></i>
                {t("employees.email_optional_hint")}
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label small">Должность</label>
              <input className="form-control" name="position" value={form.position}
                     onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="emp-position" />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Роль</label>
              <select className="form-select" name="role" value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role">
                <option value={ROLES.EMPLOYEE}>Сотрудник</option>
                <option value={ROLES.MANAGER}>Начальник</option>
                <option value={ROLES.HR}>Отдел кадров</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small">
                {t("employees.salary_label", { mode: form.salary_mode === "net" ? t("employees.salary_net") : t("employees.salary_gross") })}
              </label>
              <div className="input-group">
                <input type="number" className="form-control" name="salary" min={0} value={form.salary}
                       onChange={(e) => setForm({ ...form, salary: e.target.value })} data-testid="emp-salary" />
                <button type="button" className="btn btn-outline-secondary"
                        title={t("employees.salary_mode_toggle_hint")}
                        onClick={() => setForm({ ...form, salary_mode: form.salary_mode === "net" ? "gross" : "net" })}
                        data-testid="emp-salary-mode-toggle">
                  {form.salary_mode === "net" ? "net" : "gross"} ⇄
                </button>
              </div>
              {form.salary_mode === "net" && company?.tax_config && (
                <div className="small text-muted mt-1" data-testid="emp-net-hint">
                  {t("employees.gross_will_be", { gross: solveGrossFromNet(Number(form.salary) || 0, company.tax_config).toLocaleString("ru-RU") })}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label small">Неделя</label>
              <select className="form-select" name="week_type" value={form.week_type}
                      onChange={(e) => setForm({ ...form, week_type: e.target.value })}>
                <option value={5}>5 дней</option>
                <option value={6}>6 дней</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small">Начальник</label>
              <select className="form-select" name="manager_id" value={form.manager_id}
                      onChange={(e) => setForm({ ...form, manager_id: e.target.value })} data-testid="emp-manager">
                <option value="">— нет —</option>
                {managers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small">{t("employees.table.location")}</label>
              <select className="form-select" value={form.location_id}
                      onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                      data-testid="emp-location">
                <option value="">— {t("employees.location_none")} —</option>
                {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </div>
            {(company?.social_statuses || []).length > 0 && (
              <div className="col-md-3">
                <label className="form-label small">{t("social_status.label")}</label>
                <select className="form-select" value={form.social_status_id}
                        onChange={(e) => setForm({ ...form, social_status_id: e.target.value })}
                        data-testid="emp-social-status">
                  <option value="">— {t("social_status.none")} —</option>
                  {(company.social_statuses || [])
                    .filter(s => s.enabled !== false)
                    .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div className="col-12">
              <button className="btn btn-success" disabled={busy} data-testid="emp-save">
                {busy ? "Создание…" : "Создать"}
              </button>
              <small className="text-muted ms-3">
                Сотрудник может войти по e-mail и паролю или задать пароль через «Забыли пароль».
              </small>
            </div>
          </form>
        </div></div>
      )}

      <div className="card card-soft">
        {locations.length > 0 && (
          <div className="card-header bg-light d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-geo-alt text-primary"></i>
              <span className="small text-muted">{t("employees.filter_location")}:</span>
              <select className="form-select form-select-sm" style={{ width: 240 }}
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      data-testid="emp-location-filter">
                <option value="all">{t("employees.location_all")} ({users.length})</option>
                <option value="_none">{t("employees.location_none")} ({users.filter(u => !u.location_id).length})</option>
                {locations.map(l => (
                  <option key={l.location_id} value={l.location_id}>
                    {l.name} ({users.filter(u => u.location_id === l.location_id).length})
                  </option>
                ))}
              </select>
            </div>
            <div className="small text-muted">
              {t("employees.shown_count", { n: filteredUsers.length, total: users.length })}
            </div>
          </div>
        )}
        <div className="table-responsive">
          <table className="table align-middle mb-0" data-testid="employees-table">
            <thead className="table-light">
              <tr>
                <th>{t("employees.table.name")}</th>
                <th>{t("employees.table.email")}</th>
                <th>{t("employees.table.position")}</th>
                <th>{t("employees.table.role")}</th>
                <th>{t("employees.table.manager")}</th>
                <th>{t("employees.table.location")}</th>
                {(company?.social_statuses || []).length > 0 && (
                  <th>{t("social_status.label")}</th>
                )}
                <th>{t("employees.table.salary")}</th>
                <th>{t("employees.table.vacation_days")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.user_id} data-testid={`emp-row-${u.user_id}`}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {u.photo_b64 || u.picture ? (
                        <img src={u.photo_b64 || u.picture} alt="" width="32" height="32" className="rounded-circle" style={{ objectFit: "cover" }} />
                      ) : (
                        <div className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                             style={{ width: 32, height: 32 }}><i className="bi bi-person"></i></div>
                      )}
                      <span>{u.name}</span>
                    </div>
                  </td>
                  <td className="small" data-label={t("common.email")}>
                    {u.email ? (
                      u.email
                    ) : (
                      <button type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setBindTarget(u)}
                              data-testid={`emp-bind-invite-${u.user_id}`}>
                        <i className="bi bi-envelope-plus me-1"></i>
                        {t("employees.invite_bind")}
                      </button>
                    )}
                  </td>
                  <td className="small" data-label={t("common.position")}>{u.position || "—"}</td>
                  <td data-label={t("common.role")}>
                    <select className="form-select form-select-sm" value={u.role}
                            disabled={u.user_id === user.user_id}
                            onChange={(e) => onChangeRole(u.user_id, e.target.value)}>
                      <option value={ROLES.EMPLOYEE}>{t("roles.employee")}</option>
                      <option value={ROLES.MANAGER}>{t("roles.manager")}</option>
                      <option value={ROLES.HR}>{t("roles.hr")}</option>
                      <option value={ROLES.DIRECTOR}>{t("roles.director")}</option>
                    </select>
                  </td>
                  <td data-label={t("employees.table.manager")}>
                    <select className="form-select form-select-sm" value={u.manager_id || ""}
                            onChange={(e) => onChangeManager(u.user_id, e.target.value)}>
                      <option value="">—</option>
                      {managers.filter(m => m.user_id !== u.user_id).map(m =>
                        <option key={m.user_id} value={m.user_id}>{m.name}</option>
                      )}
                    </select>
                  </td>
                  <td data-label={t("employees.table.location")}>
                    <select className="form-select form-select-sm" value={u.location_id || ""}
                            onChange={(e) => onChangeLocation(u.user_id, e.target.value)}
                            data-testid={`emp-location-${u.user_id}`}>
                      <option value="">—</option>
                      {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                    </select>
                  </td>
                  {(company?.social_statuses || []).length > 0 && (
                    <td data-label={t("social_status.label")}>
                      <select className="form-select form-select-sm" value={u.social_status_id || ""}
                              onChange={(e) => onChangeSocialStatus(u.user_id, e.target.value)}
                              data-testid={`emp-social-status-${u.user_id}`}>
                        <option value="">—</option>
                        {(company.social_statuses || [])
                          .filter(s => s.enabled !== false)
                          .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                  )}
                  <td data-label={t("employees.table.salary")}>
                    <SalaryCell user={u} company={company}
                                onSalary={(v) => onChangeSalary(u.user_id, v)}
                                onMode={(m) => onChangeSalaryMode(u.user_id, m)} />
                  </td>
                  <td data-label={t("employees.table.vacation_days")}>
                    <input type="number" className="form-control form-control-sm" style={{ width: 72 }}
                           min={24} placeholder="24"
                           defaultValue={u.vacation_days_limit ?? ""}
                           onBlur={(e) => onChangeVacationDays(u.user_id, e.target.value)}
                           data-testid={`emp-vacation-days-${u.user_id}`} />
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={(company?.social_statuses || []).length > 0 ? 9 : 8} className="text-center text-muted py-4">
                  {users.length === 0 ? "Пока нет сотрудников" : t("employees.no_match_filter")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function InviteByEmailModal({ onClose, onSent }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [role, setRole] = useState(ROLES.EMPLOYEE);
  const [salary, setSalary] = useState(200000);
  const [weekType, setWeekType] = useState(5);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await sendInvitationApi({
        email: email.trim(),
        name: "",
        position: position.trim(),
        role,
        salary: Number(salary) || 0,
        week_type: Number(weekType) || 5,
        message: message.trim(),
      });
      onSent(email.trim());
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Ошибка отправки");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal d-block" tabIndex="-1"
         style={{ background: "rgba(0,0,0,0.5)", overflowY: "auto" }}
         data-testid="invite-email-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="modal-content">
          <form onSubmit={submit}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-envelope-plus me-2 text-primary"></i>
                {t("employees.invite_email_modal_title")}
              </h5>
              <button type="button" className="btn-close" onClick={onClose}
                      data-testid="invite-email-close"></button>
            </div>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-2">{err}</div>}
              <div className="alert alert-light py-2 small">
                <i className="bi bi-info-circle me-1"></i>
                {t("employees.invite_email_hint")}
              </div>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small">{t("employees.invite_email_label")} <span className="text-danger">*</span></label>
                  <input type="email" className="form-control" value={email}
                         required autoFocus
                         onChange={(e) => setEmail(e.target.value)}
                         placeholder="employee@example.com"
                         data-testid="invite-email-input" />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t("directory.invite_position")}</label>
                  <input className="form-control" value={position}
                         onChange={(e) => setPosition(e.target.value)}
                         data-testid="invite-email-position" />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">{t("directory.invite_role")}</label>
                  <select className="form-select" value={role}
                          onChange={(e) => setRole(e.target.value)}
                          data-testid="invite-email-role">
                    <option value={ROLES.EMPLOYEE}>{t("roles.employee")}</option>
                    <option value={ROLES.MANAGER}>{t("roles.manager")}</option>
                    <option value={ROLES.HR}>{t("roles.hr")}</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">{t("directory.invite_salary")}</label>
                  <input type="number" className="form-control" value={salary}
                         onChange={(e) => setSalary(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">{t("history.rehire_week")}</label>
                  <select className="form-select" value={weekType}
                          onChange={(e) => setWeekType(e.target.value)}>
                    <option value={5}>{t("directory.invite_week_5")}</option>
                    <option value={6}>{t("directory.invite_week_6")}</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small">{t("directory.invite_message")}</label>
                  <textarea className="form-control" rows={2} value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={t("directory.invite_message_ph")}
                            data-testid="invite-email-message" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}
                      data-testid="invite-email-submit">
                <i className="bi bi-send me-1"></i>
                {busy ? t("common.sending") : t("employees.invite_email_send")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function SalaryCell({ user, company, onSalary, onMode }) {
  const { t } = useTranslation();
  const mode = user.salary_mode === "net" ? "net" : "gross";
  const grossPreview = mode === "net" && company?.tax_config
    ? solveGrossFromNet(Number(user.salary) || 0, company.tax_config)
    : null;
  return (
    <div style={{ minWidth: 160 }}>
      <div className="input-group input-group-sm">
        <input type="number" className="form-control" style={{ width: 100 }}
               defaultValue={user.salary}
               onBlur={(e) => onSalary(e.target.value)}
               data-testid={`emp-salary-${user.user_id}`} />
        <button type="button"
                className={`btn btn-sm ${mode === "net" ? "btn-outline-warning" : "btn-outline-secondary"}`}
                title={t("employees.salary_mode_toggle_hint")}
                onClick={() => onMode(mode === "net" ? "gross" : "net")}
                data-testid={`emp-salary-mode-${user.user_id}`}>
          {mode}
        </button>
      </div>
      {grossPreview !== null && (
        <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
          {t("employees.gross_short", { gross: grossPreview.toLocaleString("ru-RU") })}
        </div>
      )}
    </div>
  );
}


/* ----------------- BIND-INVITE MODAL ----------------- */
/* Sends an invitation that, on accept/registration, attaches the e-mail to an
   existing email-less employee (`target.user_id`). Used when HR pre-created the
   employee in the system but didn't know their e-mail yet. */
function BindInviteModal({ target, onClose, onSent }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await sendInvitationApi({
        email: email.trim(),
        name: target.name || "",
        position: target.position || "",
        role: target.role || ROLES.EMPLOYEE,
        salary: Number(target.salary || 0),
        week_type: Number(target.week_type || 5),
        message: message.trim(),
        bind_user_id: target.user_id,
      });
      onSent(email.trim());
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="modal d-block" tabIndex="-1"
         style={{ background: "rgba(0,0,0,0.5)", overflowY: "auto" }}
         data-testid="bind-invite-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="modal-content">
          <form onSubmit={submit}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-envelope-plus me-2 text-primary"></i>
                {t("employees.bind_invite_title", { name: target.name })}
              </h5>
              <button type="button" className="btn-close" onClick={onClose}
                      data-testid="bind-invite-close"></button>
            </div>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-2">{err}</div>}
              <div className="alert alert-light py-2 small">
                <i className="bi bi-info-circle me-1"></i>
                {t("employees.bind_invite_hint")}
              </div>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small">
                    {t("employees.invite_email_label")} <span className="text-danger">*</span>
                  </label>
                  <input type="email" className="form-control" value={email}
                         required autoFocus
                         onChange={(e) => setEmail(e.target.value)}
                         placeholder="employee@example.com"
                         data-testid="bind-invite-email-input" />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t("directory.invite_message")}</label>
                  <textarea className="form-control" rows={2} value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={t("directory.invite_message_ph")}
                            data-testid="bind-invite-message" />
                </div>
                <div className="col-12 small text-muted">
                  <div>{t("employees.bind_invite_summary")}:</div>
                  <ul className="mb-0 ps-3">
                    <li><strong>{target.name}</strong></li>
                    {target.position && <li>{target.position}</li>}
                    <li>{t(`roles.${target.role}`)}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}
                      data-testid="bind-invite-submit">
                <i className="bi bi-send me-1"></i>
                {busy ? t("common.sending") : t("employees.invite_email_send")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
